import React, { FC } from 'react';

import { PageLoader, RoleHelper, UserRole } from '@/Shared';
import { useAppSelector, usePermission } from '@/OneLiners';
import { Navigate } from 'react-router-dom';

interface AuthGuardProps {
	children: JSX.Element;
	requiredRole: UserRole
}

/**
 * AuthGuard
 * ---------
 * Route guard responsible for enforcing authentication and role-based authorization.
 *
 * This guard applies a strict, declarative access control flow:
 * 1. If no user session exists, the user is redirected to the login page.
 * 2. If a session exists but the user's role does not meet the required level,
 *    the user is redirected to the home page.
 * 3. Only authenticated and authorized users can access the protected content.
 *
 * Design principles:
 * - Declarative navigation using `<Navigate />`
 * - No side effects or imperative routing logic
 * - Guards are evaluated during render, not after
 *
 * Typical usage:
 * <AuthGuard requiredRole={UserRole.ADMIN}>
 *   <AdminDashboard />
 * </AuthGuard>
 *
 * Author: Samano CASTRE
 * Created: July 16, 2025
 */
const AuthGuard: FC<AuthGuardProps> = ({ children, requiredRole }) => {

	/**
	 * Retrieves the current user session from the global application state.
	 * A falsy value indicates that the user is not authenticated.
	 */
	const userSession = useAppSelector(state => state.sessionState.userSession);
	const appIsReady = useAppSelector(state => state.appState.appIsReady);

	if (!appIsReady) {
		return <PageLoader message="[Auth] - Vérifiaction en cours. Veuillez patienter. Merci" />;
	}

	/**
	 * No active session:
	 * redirect the user to the login page.
	 */
	if (!userSession) {
		return <Navigate to="/login" replace />;
	}

	/**
	 * Session exists:
	 * validate that the user's role meets the minimum required role.
	 */
	const userRole = userSession.createdByUser?.role;
	const isAuthorized = RoleHelper.hasAtLeastRole(requiredRole, userRole as UserRole);

	/**
	 * Insufficient permissions:
	 * redirect the user to the home page.
	 */
	if (!isAuthorized) {
		return <Navigate to="/home" replace />;
	}

	/**
	 * Authentication and authorization checks passed:
	 * render the protected content.
	 */
	return children;
};

export default AuthGuard;